import React from 'react';
import CrudPage from '../../../components/CrudPage';
import useLookups from '../../../hooks/useLookups';
import { Button, Badge } from '../../../components/common';
import { apiError } from '../../../services/api';
import { AUDIT_COLUMNS, accountCodeLabel } from '../../../config/columns';
import {
  bankAccountService,
  currencyService,
  accountCodeService,
} from '../../../services/resources';

export default function BankAccountPage({ onToast }) {
  const lookups = useLookups({
    currencies: { service: currencyService, labelKey: (r) => r.currency_code },
    accountCodes: { service: accountCodeService, labelKey: accountCodeLabel },
  });

  const labelFor = (name, id) => lookups[name]?.find((o) => o.value === id)?.label ?? id;

  const toggleBlock = async (row, reload) => {
    try {
      if (row.is_blocked) {
        await bankAccountService.unblock(row.id);
        onToast?.('Bank account unblocked');
      } else {
        await bankAccountService.block(row.id);
        onToast?.('Bank account blocked');
      }
      reload();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  return (
    <CrudPage
      title="Bank Account"
      formCode="BANK_ACCOUNT"
      service={bankAccountService}
      onToast={onToast}
      lookups={lookups}
      searchKeys={['bank_name', 'description']}
      columns={[
        { key: 'bank_name', label: 'Bank Name' },
        { key: 'currency_id', label: 'Currency', render: (r) => labelFor('currencies', r.currency_id) },
        { key: 'account_code_id', label: 'Account Code', render: (r) => labelFor('accountCodes', r.account_code_id) },
        {
          key: 'is_blocked',
          label: 'Status',
          render: (r) =>
            r.is_blocked ? <Badge tone="red">Blocked</Badge> : <Badge tone="green">Active</Badge>,
        },
        ...AUDIT_COLUMNS,
      ]}
      fields={[
        // bank_name (uppercase) and currency lock on edit; account code stays editable.
        { key: 'bank_name', label: 'Bank Name', type: 'text', required: true, immutableOnEdit: true, uppercase: true },
        { key: 'currency_id', label: 'Currency', type: 'select', required: true, lookup: 'currencies', immutableOnEdit: true },
        { key: 'account_code_id', label: 'Account Code', type: 'select', required: true, lookup: 'accountCodes' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]}
      rowActions={(row, { reload }) => (
        <Button
          size="sm"
          variant={row.is_blocked ? 'secondary' : 'ghost'}
          onClick={() => toggleBlock(row, reload)}
        >
          {row.is_blocked ? 'Unblock' : 'Block'}
        </Button>
      )}
    />
  );
}
